/**
 * The draft's exercises outside the catalogue as board E's list draws them (canon
 * `docs/design/ai-buddy-2026-10-09-proposed-exercise/`, ai-plan-builder R8): which rows stand
 * resolved, what each one's title says happened, which row is open, the counter, what „Dodaj
 * wszystkie szybko" adds and leaves, and when the draft opens by itself.
 *
 * Pure: no React, no i18next. The server's decisions come in; what the trainer did on THIS screen
 * comes in beside them, by name: the rows reopened with „Zmień", and how a decision was reached
 * (a quick add and the form both end in a pick of the new exercise, so the server cannot tell them
 * from a pick of an existing one).
 */

import type { AiExerciseCandidate, AiProposedExercise, AiUnresolvedExercise } from '~/entities/ai-conversation';
import { MAX_EXERCISE_NAME_LENGTH, exerciseTypeFromApi } from '~/entities/exercise-library';

import type { AddFailure, DecisionRoute, OpenRow } from './exerciseDecisions';

/** What a resolved row's title states. `how: null` is an addition this screen did not see made. */
export type RowOutcome =
  | { readonly kind: 'skipped' }
  | { readonly kind: 'added'; readonly how: 'quick' | 'verified' | null }
  | { readonly kind: 'picked'; readonly target: string };

export interface OutsideCatalogueRow {
  /** The name as the list draws and adds it: its proposal is `usableProposal`'s, never the wire's own. */
  readonly exercise: AiUnresolvedExercise;
  /** Decided and not reopened: drawn as its outcome, with „Zmień". */
  readonly resolved: boolean;
  /** Decided, and opened again with „Zmień": its decision stands until another replaces it. */
  readonly reopened: boolean;
  /** What the decision was, while one stands; `null` for an undecided name. */
  readonly outcome: RowOutcome | null;
  readonly expanded: boolean;
  /** A decision about it is on its way: its controls wait, and it stays as it is until it lands. */
  readonly writing: boolean;
  /** Its last quick add did not reach the catalogue, and why. */
  readonly failure: AddFailure | null;
  /** Too long to be stored as it is written: it reaches the catalogue only through the creator. */
  readonly nameTooLong: boolean;
  /**
   * „Szybkie dodanie" is offered: a usable proposal under a name the catalogue can store, which the
   * server has not refused. Sending refused values again would be refused again (owner, R8).
   */
  readonly quickAddable: boolean;
  /** The server refused the proposal's values: „Zweryfikuj i dodaj" leads, where they can be corrected. */
  readonly verifyLeads: boolean;
  /**
   * What „Albo wybierz podobne:" offers, in order; empty when the matching found nothing close enough
   * (its verdict `none`), so no list is drawn and the catalogue search is what stays.
   */
  readonly similar: readonly AiExerciseCandidate[];
}

export interface OutsideCatalogueView {
  readonly rows: readonly OutsideCatalogueRow[];
  readonly total: number;
  /** Rows drawn resolved: „Rozwiązane n z N". */
  readonly resolvedCount: number;
  /** Rows still waiting for a decision, a reopened one included: „Brakujące w katalogu". */
  readonly remaining: number;
  readonly allResolved: boolean;
  /**
   * What „Dodaj wszystkie szybko" adds: the waiting rows it can add as they stand, none mid-write and
   * none the server refused, in list order.
   */
  readonly batchable: readonly AiUnresolvedExercise[];
  /** The waiting rows without a usable proposal, named in its dialog and left for the form. */
  readonly withoutProposal: readonly AiUnresolvedExercise[];
  /** The waiting rows whose name is too long to store, named in its dialog and left for the form. */
  readonly nameTooLong: readonly AiUnresolvedExercise[];
  /** Board E offers the batch from two waiting rows, when at least one of them can be added. */
  readonly canBatch: boolean;
  /** A decision is on its way: the batch waits for it rather than racing it. */
  readonly batchRefused: boolean;
}

/**
 * The proposal as this build can show it and send it, or `null` when it cannot be added as it
 * stands. A type the creation does not take (`Custom`) or does not know (a newer server's) leaves
 * only the form, where the trainer chooses one: drawing „Inne" and storing `Strength` would add
 * something other than what the trainer read. Equipment this build cannot name, or that carries no
 * id the creation takes, is dropped from both the strip and the request, so nothing is sent that was
 * not shown.
 */
export function usableProposal(proposal: AiProposedExercise | null, knownEquipment: (code: string) => boolean): AiProposedExercise | null {
  if (proposal === null) return null;
  const type = exerciseTypeFromApi(proposal.exerciseType);
  if (type === null || type === 'custom') return null;
  return { ...proposal, equipment: proposal.equipment.filter((piece) => piece.id > 0 && knownEquipment(piece.code)) };
}

/** Folded for comparing a written name with a catalogue one: case and surrounding space only. */
function same(a: string, b: string): boolean {
  return a.trim().toLocaleLowerCase('pl') === b.trim().toLocaleLowerCase('pl');
}

/**
 * The outcome a decided name states. What this screen saw done wins; otherwise the server's
 * decision is read: a skip, a pick of one of the listed exercises, or the exercise its slots now
 * carry. That exercise wearing the written name itself is the one added for it.
 */
export function rowOutcome(exercise: AiUnresolvedExercise, route: DecisionRoute | undefined): RowOutcome | null {
  const { decision } = exercise;
  if (decision === null) return null;
  if (decision.kind === 'skip') return { kind: 'skipped' };
  if (route?.kind === 'pick') return { kind: 'picked', target: route.target };
  if (route !== undefined) return { kind: 'added', how: route.kind };
  const listed = exercise.candidates.find((candidate) => candidate.exerciseId === decision.exerciseId);
  if (listed !== undefined) return { kind: 'picked', target: listed.name };
  if (exercise.pickedName === null || same(exercise.pickedName, exercise.name)) return { kind: 'added', how: null };
  return { kind: 'picked', target: exercise.pickedName };
}

export interface OutsideCatalogueInput {
  readonly exercises: readonly AiUnresolvedExercise[];
  /** Names reopened with „Zmień". */
  readonly reopened: ReadonlySet<string>;
  /** How this screen decided each name it decided, by name. */
  readonly routes: ReadonlyMap<string, DecisionRoute>;
  /** Names whose decision is on its way. */
  readonly writing: ReadonlyMap<string, unknown>;
  /** Names whose last quick add failed, and why. */
  readonly failures: ReadonlyMap<string, AddFailure>;
  readonly open: OpenRow;
  /** Equipment this build can name, by its catalogue code. */
  readonly knownEquipment: (code: string) => boolean;
  /** Any decision on this screen is on its way, in this list or another. */
  readonly anyWriting: boolean;
}

/**
 * One row open at a time. A chosen row stays open while it waits; once it is decided the first
 * waiting row opens in its place, so the next decision is one click away. „none" is the trainer
 * closing the open row, and nothing opens by itself after that.
 */
export function outsideCatalogueView(input: OutsideCatalogueInput): OutsideCatalogueView {
  const { reopened, routes, writing, failures, open } = input;
  const exercises = input.exercises.map((exercise) => ({ ...exercise, proposedExercise: usableProposal(exercise.proposedExercise, input.knownEquipment) }));
  const waiting = (exercise: AiUnresolvedExercise) => exercise.decision === null || reopened.has(exercise.name);
  const tooLong = (exercise: AiUnresolvedExercise) => exercise.name.trim().length > MAX_EXERCISE_NAME_LENGTH;
  const refused = (exercise: AiUnresolvedExercise) => failures.get(exercise.name) === 'rejected';
  const quickAddable = (exercise: AiUnresolvedExercise) => exercise.proposedExercise !== null && !tooLong(exercise) && !refused(exercise);
  const chosen = open.kind === 'row' ? exercises.find((exercise) => exercise.name === open.name) : undefined;
  const expandedName =
    open.kind === 'none'
      ? null
      : chosen !== undefined && waiting(chosen)
        ? chosen.name
        : (exercises.find(waiting)?.name ?? null);
  const rows = exercises.map((exercise) => ({
    exercise,
    resolved: !waiting(exercise),
    reopened: exercise.decision !== null && reopened.has(exercise.name),
    outcome: rowOutcome(exercise, routes.get(exercise.name)),
    expanded: exercise.name === expandedName,
    writing: writing.has(exercise.name),
    failure: failures.get(exercise.name) ?? null,
    nameTooLong: tooLong(exercise),
    quickAddable: quickAddable(exercise),
    verifyLeads: refused(exercise),
    similar: exercise.verdict === 'none' ? [] : exercise.candidates,
  }));
  const pending = exercises.filter(waiting);
  const addable = pending.filter(quickAddable);
  return {
    rows,
    total: exercises.length,
    resolvedCount: exercises.length - pending.length,
    remaining: pending.length,
    allResolved: pending.length === 0,
    batchable: addable.filter((exercise) => !writing.has(exercise.name)),
    withoutProposal: pending.filter((exercise) => exercise.proposedExercise === null),
    nameTooLong: pending.filter((exercise) => exercise.proposedExercise !== null && tooLong(exercise)),
    canBatch: pending.length >= 2 && addable.length > 0,
    batchRefused: input.anyWriting,
  };
}

/** How long the resolved list stands before the draft opens by itself (board E: „po chwili"). */
export const AUTO_OPEN_DELAY_MS = 1000;

export interface AutoOpenInput {
  /** The trainer decided a name of this draft on this screen, and nothing has opened it since. */
  readonly armed: boolean;
  readonly view: OutsideCatalogueView;
  /** Values of an imported sheet still to decide (R28): the trainer has more to read first. */
  readonly valuesToDecide: number;
  /** A decision of this draft is still on its way to the server. */
  readonly writing: boolean;
}

/**
 * Whether the draft opens in the builder by itself (owner, board E): only after the trainer's own
 * last decision here has landed, never on a draft that was already resolved when the screen
 * opened, and never while the import still has values to decide.
 */
export function autoOpenDue({ armed, view, valuesToDecide, writing }: AutoOpenInput): boolean {
  return armed && view.total > 0 && view.allResolved && valuesToDecide === 0 && !writing;
}
